/**
 * The Users page row actions that need no dialog.
 */
import { reactivateUser, resendInvite, UserMgmtError } from '../../../api/users';
import type { ToastApi } from '../../../shared/toast/toastContext';

import type { UserRow } from './userViewModel';

/** Reactivates a user, reporting any failure through `window.alert`. */
export async function handleReactivate(
  user: UserRow,
  onMutationSuccess: () => void,
): Promise<void> {
  try {
    await reactivateUser(user.id);
    onMutationSuccess();
  } catch (err) {
    const message =
      err instanceof UserMgmtError
        ? err.message
        : err instanceof Error
          ? err.message
          : 'Reactivate failed';
    window.alert(message);
  }
}

function resendFailureText(err: unknown): string {
  if (err instanceof UserMgmtError && err.code === 'NOT_INVITED') {
    return 'This invitation was already accepted or revoked. Refreshing the list.';
  }
  return err instanceof Error ? err.message : 'Resend failed';
}

/** Re-sends a pending invite, toasts the outcome and refetches the list. */
export async function handleResendInvite(
  user: UserRow,
  showToast: ToastApi['showToast'],
  refetch: () => Promise<void>,
): Promise<void> {
  const to = user.email ?? user.name;
  try {
    const { emailSent } = await resendInvite(user.id);
    showToast(
      emailSent
        ? { message: `Invite re-sent to ${to}.` }
        : { message: `Invite to ${to} could not be emailed. Try again shortly.`, tone: 'error' },
    );
  } catch (err) {
    showToast({ message: resendFailureText(err), tone: 'error' });
  }
  await refetch();
}
